import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStaffMe } from '../hooks/useStaffMe';
import { useToast } from '../components/ToastProvider';
import { Tooltip } from '../components/Tooltip';
import { AlertsDashboardWidget } from '../components/AlertsDashboardWidget';
import { FilterPills } from '../components/FilterPills';
import { FilterSearch } from '../components/FilterSearch';
import { StaffObwModal } from '../obw/StaffObwModal';
import type { JourneyId } from '../obw/journeys';
import {
  listAllBetaParticipants,
  resendBetaActivation,
  sendTestBeacon,
  extendBetaTrial,
  deleteBetaParticipant,
  type BetaParticipant,
  type BetaLifecycleTag,
} from '../lib/beta-sites-api';
import { useWorkingZone, zoneByCode } from '../lib/zones';
import { useZoneReadReport } from '../components/ZoneContext';
import {
  ZoneCell, ZoneFilter, ZoneFanoutWarning, matchesZone,
} from '../components/ZoneIndicators';
// #21 — the ONE mapper for staff-API failures. Never render err.message directly:
// it carries the status, the path and the response body.
import { friendlyFromUnknown } from '../lib/friendly-error';
import { formatDate } from '../lib/time-format';

/**
 * NEXA Admin landing page.
 *
 * Phase 4b base (title + Alerts + Quick actions) extended by ADR-073
 * Amendment 2 (A2.6) to the locked onboarding-dashboard mockup:
 *   - three onboarding quick-action cards (SOW / POW / BOW);
 *   - a collapsible "Beta participants" panel (catalogue FilterSearch +
 *     FilterPills + clickable column-header sort; per-row Resend on Waiting
 *     rows; ✓ Activated on activated rows; rows clear on first beacon).
 */
export function Home() {
  const meState = useStaffMe();

  if (meState.status === 'loading') {
    return <div className="portal-loading">Loading…</div>;
  }
  if (meState.status === 'error') {
    return (
      <main className="portal-admin-home">
        <div role="alert" className="portal-error">{meState.message}</div>
      </main>
    );
  }

  const { role, name } = meState.me;
  const canOnboard = role === 'super_admin' || role === 'nexa_admin';
  const canSeeAlerts = role === 'super_admin' || role === 'nexa_admin' || role === 'support';

  return (
    <main className="portal-admin-home">
      <header className="portal-admin-home-header">
        <h1>NEXA Admin</h1>
        <p className="portal-admin-home-subtitle">Platform operations overview</p>
      </header>

      {canSeeAlerts && (
        <section aria-label="Alerts" className="portal-admin-home-alerts">
          <h2>Alerts</h2>
          <AlertsDashboardWidget />
        </section>
      )}

      {canOnboard ? (
        <DashboardOnboarding consultantName={name} />
      ) : (
        <section aria-label="Quick actions" className="portal-admin-home-quickactions">
          <h2>Quick actions</h2>
          <p className="portal-admin-home-no-actions">No quick actions available for this role.</p>
        </section>
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Onboarding quick-action cards (SOW / POW / BOW)
// ---------------------------------------------------------------------------

// DashboardOnboarding — the staff-dashboard context layer (ADR-076). It owns the
// in-place OBW modal mount and the launch context/return target: a journey
// mounts over this dashboard and returns to it (modal dismiss + toast), never a
// subscriber-portal tab. It also owns the beta-panel refresh signal so a freshly
// provisioned participant appears without a manual reload.
function DashboardOnboarding({ consultantName }: { consultantName?: string }) {
  const [modalJourney, setModalJourney] = useState<JourneyId | null>(null);
  // C3 — the B2S (Upgrade) mount needs the target beta account; held separately
  // from the account-less BOW/SOW/POW journeys so the modal can mint its token.
  const [upgradeTarget, setUpgradeTarget] = useState<BetaParticipant | null>(null);
  const [betaReloadKey, setBetaReloadKey] = useState(0);
  /**
   * ⭐⭐ ADR-124 D5 — THERE IS NO "CREATE IN ZONE" CHOICE HERE ANY MORE, BY DESIGN.
   *
   * The PR #486 picker asked the consultant to choose a datacentre. The customer's COUNTRY now
   * decides the zone, at the journey's own `residency` step — the same question the customer
   * answers on the public form — so a UK participant is created in the US whatever zone the
   * consultant is working in. What the journey still needs from here is the console's WORKING
   * zone (ADR-123 D3 precedence: preferred, else home), so its residency step can say "This
   * account will be created in {Zone}." when the two differ.
   */
  const workingZone = useWorkingZone();

  return (
    <>
      <OnboardingQuickActions
        onStartBow={() => setModalJourney('bow')}
        onStartSow={() => setModalJourney('sow')}
        onStartPow={() => setModalJourney('partner-apply')}
      />
      <BetaParticipantsPanel reloadKey={betaReloadKey} onUpgrade={setUpgradeTarget} />
      {modalJourney && (
        <StaffObwModal
          journeyId={modalJourney}
          // ADR-124 D5 — BOW/SOW derive their zone from the customer's country (the journey's
          // residency step); only the working zone is passed, for the "created elsewhere"
          // notice. `partner-apply` creates no account and takes neither.
          workingZone={modalJourney === 'partner-apply' ? undefined : workingZone}
          consultantName={consultantName}
          onClose={() => setModalJourney(null)}
          onProvisioned={() => setBetaReloadKey((k) => k + 1)}
        />
      )}
      {/* C3 — staff-launched B2S Upgrade mounts IN PLACE here (ADR-077
          mount-owns-exit); on completion the modal dismisses + a toast lands on
          this dashboard, and the panel refresh drops the now-non-beta row. */}
      {upgradeTarget && (
        <StaffObwModal
          journeyId="b2s"
          b2sAccountId={upgradeTarget.accountId}
          // ADR-123 HOME_ZONE sweep — an upgrade acts on an EXISTING account, which lives in
          // exactly one zone: the participant's own. With no zone passed the modal fell back to
          // the home zone, so a US participant's upgrade was minted against ap-southeast-2.
          zone={zoneByCode(upgradeTarget.zone)}
          consultantName={consultantName}
          onClose={() => setUpgradeTarget(null)}
          onProvisioned={() => setBetaReloadKey((k) => k + 1)}
        />
      )}
    </>
  );
}

function OnboardingQuickActions({
  onStartBow,
  onStartSow,
  onStartPow,
}: {
  onStartBow: () => void;
  onStartSow: () => void;
  onStartPow: () => void;
}) {
  return (
    <section aria-label="Onboarding workflows" className="portal-admin-home-onboarding">
      <h2 className="qa-section-heading">Onboarding workflows</h2>
      {/* ADR-124 D5 — no zone control: SOW and BOW ask the customer's country at their own
          `residency` step, and the zone follows from it (a UK business → United States,
          wherever the consultant is working). */}
      <div className="qa-grid">
        {/* ADR-073 / ADR-076 — SOW mounts the §9-forked card-first journey IN
            PLACE over this dashboard (StaffObwModal), provisioning at sow-complete
            via /portal/sow/provision; no longer the legacy /onboard/customer
            wizard. */}
        <button type="button" className="qa qa-button" onClick={onStartSow}>
          <div className="qa-top">
            <div className="qa-ico" aria-hidden="true">🧾</div>
            <span className="qa-tag qa-tag-sow">SOW</span>
          </div>
          <h3>Subscriber Onboarding</h3>
          <p>Create a subscriber account and authorise their first domain. Snippet emailed after activation.</p>
          <span className="qa-go">Start SOW →</span>
        </button>

        {/* ADR-076 Option 2 — POW mounts the §9-forked partner-apply journey IN
            PLACE. POW is subscriber-keyed (a partner is a subscriber-who-resells),
            so it opens with a subscriber picker and runs the application on the
            chosen subscriber's behalf; submit provisions via /portal/pow/provision.
            No longer a deep-link to the Partners queue. */}
        <button type="button" className="qa qa-button" onClick={onStartPow}>
          <div className="qa-top">
            <div className="qa-ico" aria-hidden="true">🤝</div>
            <span className="qa-tag qa-tag-pow">POW</span>
          </div>
          <h3>Partner Onboarding</h3>
          <p>Onboard an existing subscriber as a partner. Runs the suitability assessment on submit.</p>
          <span className="qa-go">Start POW →</span>
        </button>

        {/* ADR-076 — BOW mounts the §9-forked OBW engine IN PLACE over this
            dashboard (StaffObwModal); it no longer opens the subscriber portal in
            a tab. Completion/exit dismisses the modal here with a toast (U1/U3/U4). */}
        <button type="button" className="qa qa-button" onClick={onStartBow}>
          <div className="qa-top">
            <div className="qa-ico" aria-hidden="true">🧪</div>
            <span className="qa-tag qa-tag-bow">BOW</span>
          </div>
          <h3>Beta Onboarding</h3>
          <p>Admin-assisted beta participant onboarding. No card. Creates the account on completion.</p>
          <span className="qa-go">Start BOW →</span>
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Beta participants panel
// ---------------------------------------------------------------------------

type BetaSortKey =
  | 'businessName' | 'customerNumber' | 'primaryDomain' | 'onboardedAt'
  | 'consultant' | 'validated' | 'telemetry' | 'age';

// ---------------------------------------------------------------------------
// Beta status columns. Inline <svg> per the portal's established icon pattern
// (no icon library). BOW-finesse locked design (30 Jun 2026): icon-only status
// headers — Validated = user-check, Telemetry = globe (world), Status age =
// hourglass — and binary check/✕ status cells (green check on, muted ✕ off),
// replacing the earlier coloured tiles. Status Age stays the signed ±N number.
// (Supersedes the 27 Jun beta-participants-status-columns-mockup tile design;
// tooltip copy is preserved verbatim.)
// ---------------------------------------------------------------------------

/** 15px icon-only header glyphs. */
const HdrUserCheck = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="9" cy="7" r="3.2" /><path d="M3.5 20a6 6 0 0111 0" /><path d="M15 12l2 2 4-4" /></svg>
);
const HdrGlobe = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3c2.5 2.5 3.8 5.7 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.7-3.8-9S9.5 5.5 12 3z" /></svg>
);
const HdrHourglass = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 3h12" /><path d="M6 21h12" /><path d="M7 3c0 5 5 6 5 9s-5 4-5 9" /><path d="M17 3c0 5-5 6-5 9s5 4 5 9" /></svg>
);

/** 18px status-cell glyphs — binary check / ✕. */
const StatCheck = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 6.5" /></svg>
);
const StatX = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
);

/** 17px action-button glyphs (ADR-078 §3 — icon-only actions with tooltips).
 *  Inline SVG per the portal's no-icon-library convention. */
const IcoBroadcast = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="2" /><path d="M16.2 7.8a6 6 0 010 8.4M7.8 16.2a6 6 0 010-8.4M19 5a9.5 9.5 0 010 14M5 19A9.5 9.5 0 015 5" /></svg>
);
const IcoCalendarPlus = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9h18M8 2.5v4M16 2.5v4M12 12.5v5M9.5 15h5" /></svg>
);
const IcoArrowUpCircle = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 16V8M8.5 11.5L12 8l3.5 3.5" /></svg>
);
const IcoTrash = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h16M9 7V4.5h6V7M6 7l1 13h10l1-13M10 11v6M14 11v6" /></svg>
);
const IcoResend = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 11-3-6.7M21 4v4h-4" /></svg>
);

/** ADR-078 §2 — lifecycle-tag chip class per derived tag (colour tokens:
 *  BETA neutral/brand, EXPIRED warn, EXTENDED info, COMPLETED muted). */
const TAG_CLASS: Record<BetaLifecycleTag, string> = {
  BETA: 'portal-beta-chip beta-chip-beta',
  EXPIRED: 'portal-beta-chip beta-chip-expired',
  EXTENDED: 'portal-beta-chip beta-chip-extended',
  COMPLETED: 'portal-beta-chip beta-chip-completed',
};
const TAG_TOOLTIP: Record<BetaLifecycleTag, { title: string; body: string }> = {
  BETA: { title: 'Beta', body: 'Active beta trial (or awaiting the first telemetry beacon to start the 28-day clock).' },
  EXPIRED: { title: 'Expired', body: 'The 28-day trial has elapsed. Extend (+28 days, once), Upgrade, or Delete.' },
  EXTENDED: { title: 'Extended', body: 'The single +28-day extension is running. No further extension is available.' },
  COMPLETED: { title: 'Completed', body: 'The extended trial has elapsed. Remaining paths are Upgrade or Delete.' },
};

/** VALIDATED cell — step 1 (account confirmed / password set). Green check when
 *  confirmed, muted ✕ when not. */
function ValidatedCell({ validated }: { validated: boolean }) {
  return validated ? (
    <Tooltip variant="success" title="Validated" aria="Validated — account confirmed, password set." content="Account confirmed, password set.">
      <span className="beta-stat beta-stat-on" tabIndex={0} role="img" aria-label="Validated">{StatCheck}</span>
    </Tooltip>
  ) : (
    <Tooltip variant="info" title="Not validated" aria="Not validated — awaiting account confirmation." content="Awaiting account confirmation.">
      <span className="beta-stat beta-stat-off" tabIndex={0} role="img" aria-label="Not validated">{StatX}</span>
    </Tooltip>
  );
}

/** TELEMETRY cell — step 2 (snippet installed + beacons arriving). Green check
 *  when live, muted ✕ otherwise; the tooltip distinguishes "chase the install"
 *  (validated, no telemetry) from "no activity" (not validated). */
export function TelemetryCell({ telemetryLive, validated, testActive = false }: { telemetryLive: boolean; validated: boolean; testActive?: boolean }) {
  // BEACON-1 — temporary "(test)" flip after a Send Test beacon. Visually
  // distinct from a real "Telemetry live" (a "test" pill + dedicated tooltip) so
  // an operator is never misled into thinking a synthetic beacon made the domain
  // genuinely live. Takes precedence only for the ~10s flash window; the real
  // telemetry state resumes when it clears.
  if (testActive) {
    return (
      <Tooltip variant="info" title="Test beacon — temporary" aria="Test beacon — temporary signal, clears in about 10 seconds." content="Synthetic test beacon — temporary, clears in ~10s. Not a real telemetry signal.">
        <span className="beta-stat beta-stat-on beta-stat-test" tabIndex={0} role="img" aria-label="Test beacon — temporary">
          {StatCheck}<span className="beta-stat-test-pill" aria-hidden="true">test</span>
        </span>
      </Tooltip>
    );
  }
  if (telemetryLive) {
    return (
      <Tooltip variant="success" title="Telemetry live" aria="Telemetry live — snippet installed, beacons arriving." content="Snippet installed, beacons arriving.">
        <span className="beta-stat beta-stat-on" tabIndex={0} role="img" aria-label="Telemetry live">{StatCheck}</span>
      </Tooltip>
    );
  }
  if (validated) {
    return (
      <Tooltip variant="warning" title="No telemetry yet" aria="No telemetry yet — snippet not firing, chase the install." content="Snippet not firing — chase the install.">
        <span className="beta-stat beta-stat-off" tabIndex={0} role="img" aria-label="No telemetry yet">{StatX}</span>
      </Tooltip>
    );
  }
  return (
    <Tooltip variant="info" title="No telemetry" aria="No telemetry — no snippet activity." content="No snippet activity.">
      <span className="beta-stat beta-stat-off" tabIndex={0} role="img" aria-label="No telemetry">{StatX}</span>
    </Tooltip>
  );
}

/** STATUS AGE — signed triage number.
 *  ADR-078 §1.5 — once the trial clock runs (`trialDaysRemaining` non-null) the
 *  column becomes the trial countdown: +green days remaining / −red days since
 *  expiry. Before the clock starts it keeps its pre-clock meaning: +green live /
 *  −red waiting on install / "—" not validated. */
export function StatusAge({
  days,
  telemetryLive,
  trialDaysRemaining,
}: {
  days: number | null;
  telemetryLive: boolean;
  trialDaysRemaining: number | null;
}) {
  // Clock running → trial countdown to expiry.
  if (trialDaysRemaining !== null) {
    const remaining = trialDaysRemaining >= 0;
    const mag = Math.abs(trialDaysRemaining);
    const unit = mag === 1 ? 'day' : 'days';
    // BEACON-2 — zero-magnitude guard: a trial expiring TODAY is neither "+0
    // days left" nor "−0"; render a neutral 0 with no sign. (Same guard the
    // telemetry-age branch below needs — never ship "−0"/"+0".)
    if (mag === 0) {
      const body = 'Beta trial expires today. Extend, Upgrade, or Delete.';
      return (
        <Tooltip variant="warning" title="Expires today" aria={`Expires today. ${body}`} content={body}>
          <span className="beta-age beta-age-zero" tabIndex={0}>0</span>
        </Tooltip>
      );
    }
    const title = remaining ? `${mag} ${unit} left` : `Expired ${mag} ${unit} ago`;
    const body = remaining
      ? `Beta trial — ${mag} ${unit} remaining before expiry.`
      : `Beta trial expired ${mag} ${unit} ago. Extend, Upgrade, or Delete.`;
    return (
      <Tooltip variant={remaining ? 'success' : 'danger'} title={title} aria={`${title}. ${body}`} content={body}>
        <span className={`beta-age ${remaining ? 'beta-age-pos' : 'beta-age-neg'}`} tabIndex={0}>
          {remaining ? '+' : '−'}{mag}
        </span>
      </Tooltip>
    );
  }
  if (days === null) {
    return <span className="beta-age-none" aria-label="No status age — not yet validated">—</span>;
  }
  const mag = Math.abs(days);
  // BEACON-2 — zero-magnitude guard BEFORE the sign is applied. `days === 0`
  // means validated/live TODAY: neither healthy-positive nor waiting-negative,
  // so render a neutral, sign-less `0` — never the "−0" (or "+0") that the
  // unguarded `{live ? '+' : '−'}{mag}` produced for a zero row.
  if (mag === 0) {
    const live0 = telemetryLive;
    const body = live0 ? 'Telemetry firing since today. Healthy.' : 'Validated today, snippet not firing yet.';
    const title = live0 ? 'Live today' : 'Validated today';
    return (
      <Tooltip variant={live0 ? 'success' : 'warning'} title={title} aria={`${title}. ${body}`} content={body}>
        <span className="beta-age beta-age-zero" tabIndex={0}>0</span>
      </Tooltip>
    );
  }
  // Sign/basis convention (confirmed correct, not inverted): +green = live and
  // counting days-live; −red = validated but awaiting telemetry.
  const live = telemetryLive && days >= 0;
  const unit = mag === 1 ? 'day' : 'days';
  const title = live ? `Live ${mag} ${unit}` : `Waiting ${mag} ${unit}`;
  const body = live
    ? `Telemetry firing ${mag} ${unit}. Healthy.`
    : `Validated ${mag} ${unit} ago, snippet not firing.`;
  return (
    <Tooltip variant={live ? 'success' : 'danger'} title={title} aria={`${title}. ${body}`} content={body}>
      <span className={`beta-age ${live ? 'beta-age-pos' : 'beta-age-neg'}`} tabIndex={0}>
        {live ? '+' : '−'}{mag}
      </span>
    </Tooltip>
  );
}

function BetaParticipantsPanel({ reloadKey = 0, onUpgrade }: { reloadKey?: number; onUpgrade: (p: BetaParticipant) => void }) {
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [rows, setRows] = useState<BetaParticipant[]>([]);
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');
  // ADR-122 Pattern 1 — the fan-out's partial-read outcome, and the narrowing control.
  const [failedZones, setFailedZones] = useState<string[]>([]);
  const [zoneFilter, setZoneFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Set<BetaParticipant['status']>>(
    new Set(['waiting', 'activated']),
  );
  // Default: Status Age ascending — surfaces the longest-waiting (most negative)
  // participants at the top, per the locked mockup's Status Age tooltip.
  const [sort, setSort] = useState<{ key: BetaSortKey; dir: 'asc' | 'desc' }>({ key: 'age', dir: 'asc' });
  const [resent, setResent] = useState<Set<string>>(new Set());
  // UAT punchlist H — per-row "Send test beacon" in-flight state.
  const [beaconSending, setBeaconSending] = useState<Set<string>>(new Set());
  // BEACON-1 — per-row TEMPORARY test-beacon flip. `queryBetaTelemetry`
  // deliberately EXCLUDES `source = 'synthetic'` (flow-validator.ts — the C1
  // fix, so a synthetic beacon can never permanently convert a no-snippet domain
  // to "live"). So the real Telemetry cell will never flip from a test beacon —
  // by design. To still give the operator the "the beacon journey works"
  // feedback Jeff asked for, on a successful Send Test we flip THIS row's
  // Telemetry to a clearly-marked "(test)" live state for ~10s, then auto-clear
  // back to its real state, entirely client-side (no backend round-trip).
  const [testFlash, setTestFlash] = useState<Set<string>>(new Set());
  const testFlashTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  useEffect(() => {
    const timers = testFlashTimers.current;
    return () => { timers.forEach((t) => clearTimeout(t)); timers.clear(); };
  }, []);

  // `silent` (UAT punchlist G) — a background poll refresh must NOT flip the
  // panel back to the "Loading…" state or raise an error toast on a transient
  // blip; it just swaps the rows in place. The initial/explicit load keeps both.
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoadState('loading');
    try {
      // ADR-122 Pattern 1 — the alert tiles ABOVE this roster already fan out
      // (`fetchAlertCounts` in alerts-api). The roster never did, so this dashboard reported
      // platform-wide alert counts directly over a participant list that was silently one
      // jurisdiction's. This brings the panel in line with the rest of its own page.
      const r = await listAllBetaParticipants();
      setRows(r.participants);
      setFailedZones(r.failedZones);
      setLoadState('loaded');
    } catch (err) {
      if (!silent) {
        setLoadState('error');
        toast.push({
          variant: 'error',
          title: 'Could not load beta participants',
          detail: friendlyFromUnknown(err),
        });
      }
    }
  }, [toast]);

  // Reloads on mount and whenever a new participant is provisioned (reloadKey).
  useEffect(() => { void load(); }, [load, reloadKey]);

  // ADR-123 Decision 1 — the navigation bar names the zones in this roster and their counts.
  useZoneReadReport({ rows: loadState === 'loaded' ? rows : null, failedZones, filter: zoneFilter });

  // UAT punchlist G (30 Jun 2026) — auto-poll every 10s so Validated/Telemetry/
  // Status-Age update live without a manual refresh. Polls silently (no loading
  // flash); pauses while the tab is hidden and refreshes once on re-show, so a
  // backgrounded console does not hammer the API. Cleared on unmount.
  useEffect(() => {
    const POLL_MS = 10_000;
    const tick = () => { if (document.visibilityState === 'visible') void load(true); };
    const timer = setInterval(tick, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') void load(true); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  const counts = useMemo(() => ({
    waiting: rows.filter((r) => r.status === 'waiting').length,
    activated: rows.filter((r) => r.status === 'activated').length,
  }), [rows]);

  const statusPills = useMemo(() => [
    { key: 'waiting' as const, label: 'Awaiting', count: counts.waiting },
    { key: 'activated' as const, label: 'Activated', count: counts.activated },
  ], [counts]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = rows.filter((r) => {
      if (!statusFilter.has(r.status)) return false;
      if (!matchesZone(r, zoneFilter)) return false;
      if (q) {
        const hay = `${r.businessName} ${r.ownerName} ${r.customerNumber} ${r.primaryDomain} ${r.consultant}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      // Status Age: signed numeric; "—" (null) rows always sort to the bottom.
      if (sort.key === 'age') {
        const an = a.statusAgeDays;
        const bn = b.statusAgeDays;
        if (an === null && bn === null) return 0;
        if (an === null) return 1;
        if (bn === null) return -1;
        return (an - bn) * factor;
      }
      let av: string | number;
      let bv: string | number;
      if (sort.key === 'validated') { av = a.status === 'activated' ? 1 : 0; bv = b.status === 'activated' ? 1 : 0; }
      else if (sort.key === 'telemetry') { av = a.telemetryLive ? 1 : 0; bv = b.telemetryLive ? 1 : 0; }
      else { av = (a[sort.key] ?? '').toLowerCase(); bv = (b[sort.key] ?? '').toLowerCase(); }
      return av < bv ? -1 * factor : av > bv ? 1 * factor : 0;
    });
  }, [rows, search, statusFilter, zoneFilter, sort]);

  const toggleSort = (key: BetaSortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  const ariaSort = (key: BetaSortKey): 'ascending' | 'descending' | 'none' =>
    sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';

  const resend = async (p: BetaParticipant) => {
    if (!p.cognitoSub) return;
    try {
      await resendBetaActivation(p.cognitoSub, zoneByCode(p.zone));
      setResent((prev) => new Set(prev).add(p.accountId));
      toast.push({ variant: 'success', title: 'Activation resent', detail: `A fresh activation email was sent to ${p.email}.` });
    } catch (err) {
      toast.push({ variant: 'error', title: 'Resend failed', detail: friendlyFromUnknown(err) });
    }
  };

  // UAT punchlist H / BEACON-1 — fire a synthetic test beacon to exercise the
  // Telemetry journey without a live site, then TEMPORARILY flip this row's
  // Telemetry to a marked "(test)" live state for ~10s (the real query excludes
  // synthetic beacons by design — see `testFlash` above — so this client-side
  // flip is what proves the journey ran), auto-clearing back to the real state.
  const TEST_FLASH_MS = 10000;
  const testBeacon = async (p: BetaParticipant) => {
    setBeaconSending((prev) => new Set(prev).add(p.accountId));
    try {
      await sendTestBeacon(p.accountId, zoneByCode(p.zone));
      toast.push({
        variant: 'success',
        title: 'Test beacon sent',
        detail: `A synthetic (test) beacon was fired for ${p.primaryDomain || p.businessName}. Telemetry shows a temporary test signal for ${TEST_FLASH_MS / 1000}s.`,
      });
      // Temporary client-side flip — green "(test)" for ~10s, then auto-clear.
      setTestFlash((prev) => new Set(prev).add(p.accountId));
      const existing = testFlashTimers.current.get(p.accountId);
      if (existing) clearTimeout(existing);
      const timer = setTimeout(() => {
        setTestFlash((prev) => { const n = new Set(prev); n.delete(p.accountId); return n; });
        testFlashTimers.current.delete(p.accountId);
      }, TEST_FLASH_MS);
      testFlashTimers.current.set(p.accountId, timer);
    } catch (err) {
      toast.push({ variant: 'error', title: 'Test beacon failed', detail: friendlyFromUnknown(err) });
    } finally {
      setBeaconSending((prev) => { const n = new Set(prev); n.delete(p.accountId); return n; });
    }
  };

  // ── ADR-078 lifecycle exits ───────────────────────────────────────────────
  const [extending, setExtending] = useState<Set<string>>(new Set());
  // Dual-confirmation delete (§5.2): two deliberate steps before any network call.
  const [deleteTarget, setDeleteTarget] = useState<BetaParticipant | null>(null);
  const [deleteStep, setDeleteStep] = useState<1 | 2>(1);
  const [deleting, setDeleting] = useState(false);

  // Extend (+28 days) — enabled only at EXPIRED (§3.1). Server re-asserts.
  const extend = async (p: BetaParticipant) => {
    setExtending((prev) => new Set(prev).add(p.accountId));
    try {
      await extendBetaTrial(p.accountId, zoneByCode(p.zone));
      toast.push({
        variant: 'success',
        title: 'Trial extended',
        detail: `${p.businessName || 'The participant'} now has +28 days. This was the single allowed extension.`,
      });
      void load(true);
    } catch (err) {
      toast.push({ variant: 'error', title: 'Could not extend trial', detail: friendlyFromUnknown(err) });
    } finally {
      setExtending((prev) => { const n = new Set(prev); n.delete(p.accountId); return n; });
    }
  };

  // Upgrade (B2S, beta → subscriber) — C3 (ADR-077 mount-owns-exit). Hand off to
  // the dashboard owner, which mounts the §9-forked B2S journey IN PLACE over the
  // nexa-admin dashboard (StaffObwModal) and returns here on completion with a
  // toast. NO subscriber-portal tab — the row drops off (isBeta:false) on the
  // post-conversion panel refresh (onProvisioned).
  const upgrade = (p: BetaParticipant) => onUpgrade(p);

  const openDelete = (p: BetaParticipant) => { setDeleteTarget(p); setDeleteStep(1); };
  const closeDelete = () => { if (!deleting) { setDeleteTarget(null); setDeleteStep(1); } };
  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const r = await deleteBetaParticipant(deleteTarget.accountId, zoneByCode(deleteTarget.zone));
      toast.push({
        variant: r.emailSent ? 'success' : 'info',
        title: 'Beta account deleted',
        detail: r.emailSent
          ? `${deleteTarget.businessName || 'The account'} and all its data were permanently deleted. A confirmation email was sent.`
          : `${deleteTarget.businessName || 'The account'} and all its data were permanently deleted (the confirmation email did not send).`,
      });
      setDeleteTarget(null);
      setDeleteStep(1);
      void load(true);
    } catch (err) {
      toast.push({ variant: 'error', title: 'Could not delete account', detail: friendlyFromUnknown(err) });
    } finally {
      setDeleting(false);
    }
  };

  // Plain (text) sortable columns rendered in the header loop.
  // UAT punchlist I (30 Jun 2026) — Customer № is no longer its own column; it
  // is stacked beneath the org name in the Participant/Org cell (the project's
  // stacked org-identity pattern, forked from the POW picker). It remains
  // searchable (see `filtered`); only the standalone column is removed.
  const cols: Array<{ key: BetaSortKey; label: string }> = [
    { key: 'businessName', label: 'Participant / Org' },
    { key: 'primaryDomain', label: 'Domain' },
    { key: 'onboardedAt', label: 'Onboarded' },
    { key: 'consultant', label: 'Consultant' },
  ];

  // The three status columns — coloured header pills + hover cards, forked from
  // the locked mockup (copy is verbatim; do not reword).
  const statusCols: Array<{
    key: BetaSortKey; label: string; pill: string; icon: JSX.Element;
    tipTitle: string; tipBody: JSX.Element; tipAria: string;
  }> = [
    {
      key: 'validated', label: 'Validated', pill: 'beta-hdr-v', icon: HdrUserCheck,
      tipTitle: 'Validated · step 1',
      tipBody: <>The participant has confirmed their account and set their password. This is step 1 of beta onboarding.</>,
      tipAria: 'Validated, step 1 — the participant has confirmed their account and set their password.',
    },
    {
      key: 'telemetry', label: 'Telemetry', pill: 'beta-hdr-t', icon: HdrGlobe,
      tipTitle: 'Telemetry · step 2',
      tipBody: <>The tracking snippet is installed and sending data from the participant's domain. This is step 2 of beta onboarding.</>,
      tipAria: 'Telemetry, step 2 — the tracking snippet is installed and sending data from the participant’s domain.',
    },
    {
      key: 'age', label: 'Status age', pill: 'beta-hdr-a', icon: HdrHourglass,
      tipTitle: 'Status age',
      tipBody: (
        <>Days since the participant validated. <span className="beta-age-neg">Red negative</span> = validated but the snippet has not fired yet (waiting on install). <span className="beta-age-pos">Green positive</span> = days the site has been live and sending telemetry. Sort ascending to bring the longest-waiting participants to the top.</>
      ),
      tipAria: 'Status age — days since the participant validated. Red negative means waiting on install; green positive means live and sending telemetry. Sort ascending to bring the longest-waiting participants to the top.',
    },
  ];

  return (
    <section aria-label="Beta participants" className="portal-admin-home-beta">
      {/* OBW UAT r2 #2 — proper page-style heading matching the Alerts section
          (.portal-admin-home-alerts h2 / AlertsPage h1), not the small uppercase
          qa-section-heading label. */}
      <h2>Beta participants</h2>
      <div className="beta-panel">
        <button
          type="button"
          className="beta-panel-head"
          aria-expanded={open}
          aria-controls="beta-panel-body"
          onClick={() => setOpen((o) => !o)}
        >
          <span className="beta-panel-title">
            <span className="beta-chev" aria-hidden="true">{open ? '▾' : '▸'}</span> Beta status
          </span>
          <span className="beta-panel-count">
            {counts.waiting} awaiting · {counts.activated} activated · target 20
          </span>
        </button>

        {open && (
          <div id="beta-panel-body">
            <div className="beta-list-controls">
              <FilterSearch
                value={search}
                onChange={setSearch}
                placeholder="Search org, customer №, domain, consultant…"
                ariaLabel="Search beta participants"
              />
              <FilterPills
                groups={statusPills}
                selected={statusFilter}
                onChange={(next) => setStatusFilter(next as Set<BetaParticipant['status']>)}
                ariaLabel="Filter by status"
              />
              <ZoneFilter value={zoneFilter} onChange={setZoneFilter} />
            </div>

            <ZoneFanoutWarning
              failedZones={failedZones}
              noun="beta participants"
              onRetry={() => void load()}
            />

            {loadState === 'loading' && <p className="portal-muted beta-pad">Loading beta participants…</p>}
            {loadState === 'error' && (
              <p className="portal-error beta-pad" role="alert">
                Could not load beta participants.{' '}
                <button type="button" className="portal-link-button" onClick={() => void load()}>Retry</button>
              </p>
            )}

            {loadState === 'loaded' && (
              <div className="beta-table-scroll">
              <table className="beta-table is-multizone">
                {/* BOW-finesse B4/B7 + C2 locked layout (30 Jun 2026), cosmetic
                    Item 6 (1 Jul 2026) — table-layout: fixed, total 1372px (wrapped
                    in .beta-table-scroll so it never clips on a narrower viewport).
                    Participant/Org 210 (ellipsis + full-name tooltip — long org names
                    show fully on hover) · Tag 84 · Domain 250 · Onboarded 116 ·
                    Consultant 200 · Validated 84 · Telemetry 84 · Status age 84 (84px
                    clears the enlarged sort glyph) · Actions 260. C2: 5-button
                    awaiting rows (Resend/Test/Extend/Upgrade/Delete) clipped the red
                    Delete at 200px, so 60px moved Org→Actions. Item 6: domains were
                    truncating, so 50px moved Org(260→210)→Domain(200→250) — table sum
                    unchanged (1372). The <colgroup> is the single source of width;
                    .beta-table width must equal the sum. */}
                {/* ⛔ NO SIDEWAYS SCROLL (Jeff, 31 October 2026 — admin-portal-ui-standards.md §6 rule (a)). The table
                    used to be a fixed 1492 px inside an x-scroller, so at 1280 px it scrolled 278 px sideways. Now it is
                    the panel's width: every column keeps its fixed width except Participant/Org, Domain and Consultant,
                    which share what is left and ellipsise (full text on hover). Zone is 58 px — the code, not the name. */}
                <colgroup>
                  <col className="beta-col-flex" />
                  <col style={{ width: 84 }} />
                  {/* ADR-122 Pattern 1 — Zone, sitting beside Tag. The <colgroup> is the single
                      source of width and `.beta-table`'s CSS width must equal the sum, so
                      `.is-multizone` bumps 1372 → 1492 to match. ADR-123: always rendered, and
                      120px (was 92) for the region-first label. The wrapper already scrolls
                      horizontally, so the extra column cannot clip the Actions cell. */}
                  <col style={{ width: 58 }} />
                  <col className="beta-col-flex" />
                  <col style={{ width: 116 }} />
                  <col className="beta-col-flex" />
                  <col style={{ width: 84 }} />
                  <col style={{ width: 84 }} />
                  <col style={{ width: 84 }} />
                  <col style={{ width: 260 }} />
                </colgroup>
                <thead>
                  <tr>
                    {cols.map((c, i) => (
                      <Fragment key={c.key}>
                        <th
                          className="beta-th-sortable"
                          aria-sort={ariaSort(c.key)}
                          onClick={() => toggleSort(c.key)}
                        >
                          {c.label}
                          <span className="beta-arr" aria-hidden="true">
                            {sort.key === c.key ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                          </span>
                        </th>
                        {/* Dedicated Tag column (84px) for the BETA badge — split
                            out of the org cell, future-proofed for beta-stage /
                            extension tags. Non-sortable. */}
                        {i === 0 && <th className="beta-th-tag">Tag</th>}
                        {i === 0 && <th className="beta-th-zone">Zone</th>}
                      </Fragment>
                    ))}
                    {statusCols.map((c) => (
                      <th
                        key={c.key}
                        className="beta-th-sortable beta-th-status"
                        aria-sort={ariaSort(c.key)}
                        onClick={() => toggleSort(c.key)}
                      >
                        {/* Icon-only header (no text → narrow 60px column). The
                            meaning stays available to screen readers (aria-label)
                            and on hover (native title + the rich Tooltip card). */}
                        {/* B8 — the polished Tooltip now renders reliably (portaled
                            to body, no overflow-clipping), so the native `title` is
                            removed: it produced the ugly duplicate/fallback box.
                            aria-label keeps the meaning for screen readers. */}
                        <Tooltip variant="info" title={c.tipTitle} aria={c.tipAria} content={c.tipBody} anchorClassName="beta-hdr-anchor">
                          <span className={`beta-hdr beta-hdr-icononly ${c.pill}`} tabIndex={0} aria-label={c.label}>
                            {c.icon}
                          </span>
                        </Tooltip>
                        <span className="beta-arr" aria-hidden="true">
                          {sort.key === c.key ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </th>
                    ))}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={10} className="beta-empty">
                        {statusFilter.size === 0
                          ? 'No status filters selected — select Awaiting or Activated to show rows.'
                          : 'No beta participants match the current search.'}
                      </td>
                    </tr>
                  )}
                  {filtered.map((p) => {
                    const validated = p.status === 'activated';
                    const wasResent = resent.has(p.accountId);
                    return (
                      <tr key={p.accountId}>
                        {/* Stacked org identity: name (top), customer № (muted
                            mono), optional owner sub-label. BETA badge now lives
                            in its own Tag column. */}
                        <td className="beta-org">
                          {/* B4 — ellipsis-on-overflow (CSS) + full name on hover
                              so long org names never wrap/break the fixed layout. */}
                          <span className="beta-org-name" title={p.businessName}>{p.businessName}</span>
                          <span className="beta-cn">{p.customerNumber || '—'}</span>
                          {p.ownerName && <span className="beta-sub">{p.ownerName}</span>}
                        </td>
                        <td className="beta-tag-cell">
                          {(() => {
                            const tag = p.lifecycleTag ?? 'BETA';
                            const tt = TAG_TOOLTIP[tag];
                            return (
                              <Tooltip variant="info" title={tt.title} aria={`${tt.title}. ${tt.body}`} content={tt.body}>
                                <span className={TAG_CLASS[tag]} tabIndex={0}>{tag}</span>
                              </Tooltip>
                            );
                          })()}
                        </td>
                        <td className="beta-zone-cell"><ZoneCell zone={p.zone} /></td>
                        <td className="beta-mono" title={p.primaryDomain || undefined}>{p.primaryDomain || '—'}</td>
                        <td>{p.onboardedAt ? formatDate(p.onboardedAt) : '—'}</td>
                        {/* Item 8 — stacked consultant identity: full name (top),
                            email (muted, below). Reuses the .beta-org stacked
                            pattern. Email row omitted when absent (older rows,
                            recovered by the backfill). */}
                        <td className="beta-org">
                          <span className="beta-org-name" title={p.consultant}>{p.consultant || '—'}</span>
                          {p.consultantEmail && <span className="beta-cn">{p.consultantEmail}</span>}
                        </td>
                        <td className="beta-col-status"><ValidatedCell validated={validated} /></td>
                        <td className="beta-col-status"><TelemetryCell telemetryLive={p.telemetryLive} validated={validated} testActive={testFlash.has(p.accountId)} /></td>
                        <td className="beta-col-status"><StatusAge days={p.statusAgeDays} telemetryLive={p.telemetryLive} trialDaysRemaining={p.trialDaysRemaining} /></td>
                        <td className="beta-actions">
                          {/* ADR-078 §3 — icon-only actions, each with a tooltip.
                              The whole panel is super_admin/nexa_admin-only. */}
                          <div className="beta-actions-row">
                            {!validated && (
                              <Tooltip variant="info" title={wasResent ? 'Activation resent' : 'Resend activation'} aria="Resend the activation email" content={wasResent ? 'A fresh activation email has been sent.' : 'Resend the participant’s activation email.'}>
                                <button
                                  type="button"
                                  className={`beta-act-btn${wasResent ? ' beta-act-done' : ''}`}
                                  onClick={() => void resend(p)}
                                  disabled={wasResent || !p.cognitoSub}
                                  aria-label="Resend activation email"
                                >
                                  {IcoResend}
                                </button>
                              </Tooltip>
                            )}
                            <Tooltip variant="info" title="Send test beacon" aria="Send a synthetic test beacon" content="Fire a synthetic (test) beacon for this domain to exercise Telemetry without a live site.">
                              <button
                                type="button"
                                className="beta-act-btn"
                                onClick={() => void testBeacon(p)}
                                disabled={beaconSending.has(p.accountId)}
                                aria-label="Send test beacon"
                              >
                                {IcoBroadcast}
                              </button>
                            </Tooltip>
                            <Tooltip
                              variant={p.lifecycleTag === 'EXPIRED' ? 'warning' : 'info'}
                              title="Extend trial +28 days"
                              aria="Extend the beta trial by 28 days"
                              content={p.lifecycleTag === 'EXPIRED' ? 'Extend the expired trial by 28 days (the single allowed extension).' : 'Extend is available only once the trial has EXPIRED.'}
                            >
                              <button
                                type="button"
                                className="beta-act-btn"
                                onClick={() => void extend(p)}
                                disabled={p.lifecycleTag !== 'EXPIRED' || extending.has(p.accountId)}
                                aria-label="Extend trial by 28 days"
                              >
                                {IcoCalendarPlus}
                              </button>
                            </Tooltip>
                            <Tooltip variant="info" title="Upgrade to subscriber" aria="Upgrade this beta participant to a paying subscriber" content="Convert this beta participant to a paying subscriber. Runs in place on this dashboard.">
                              <button
                                type="button"
                                className="beta-act-btn"
                                onClick={() => upgrade(p)}
                                aria-label="Upgrade to subscriber"
                              >
                                {IcoArrowUpCircle}
                              </button>
                            </Tooltip>
                            <Tooltip variant="danger" title="Delete account" aria="Delete this beta account and all its data" content="Permanently delete this beta account and all its data. Two confirmations required.">
                              <button
                                type="button"
                                className="beta-act-btn beta-act-danger"
                                onClick={() => openDelete(p)}
                                aria-label="Delete beta account"
                              >
                                {IcoTrash}
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}
      </div>
      <p className="beta-note">
        Validated = account confirmed; Telemetry = the snippet is live and sending beacons. Status Age
        triages by sign — red negative = waiting on the install, green positive = days live. Once the
        trial clock runs it becomes the trial countdown (days remaining / since expiry). Full beta
        list lives under <Link to="/admin/customers">Customers</Link> (beta accounts now show there by default).
      </p>

      {/* ADR-078 §5.2 — dual-confirmation delete. Two deliberate steps, never
          one-click from the trash icon; no network call until step 2 is confirmed. */}
      {deleteTarget && (
        <div className="beta-modal-overlay" role="dialog" aria-modal="true" aria-label="Delete beta account">
          <div className="beta-modal">
            {deleteStep === 1 ? (
              <>
                <h3 className="beta-modal-title">Delete this beta account?</h3>
                <p className="beta-modal-body">
                  You are about to delete <strong>{deleteTarget.businessName || 'this beta account'}</strong>
                  {deleteTarget.customerNumber ? ` (${deleteTarget.customerNumber})` : ''}. This removes the
                  participant’s login, domains, and all associated data.
                </p>
                <div className="beta-modal-actions">
                  <button type="button" className="beta-modal-cancel" onClick={closeDelete}>Cancel</button>
                  <button type="button" className="beta-modal-danger" onClick={() => setDeleteStep(2)}>Continue</button>
                </div>
              </>
            ) : (
              <>
                <h3 className="beta-modal-title">All data will be deleted and unrecoverable</h3>
                <p className="beta-modal-body">
                  This permanently deletes <strong>{deleteTarget.businessName || 'this beta account'}</strong> and
                  every record tied to it. <strong>This cannot be undone.</strong> A confirmation email is sent to
                  the participant.
                </p>
                <div className="beta-modal-actions">
                  <button type="button" className="beta-modal-cancel" onClick={closeDelete} disabled={deleting}>Cancel</button>
                  <button type="button" className="beta-modal-danger" onClick={() => void doDelete()} disabled={deleting}>
                    {deleting ? 'Deleting…' : 'Delete permanently'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
